// Manager oversight (Supervisión) data — orchestrator M4 §4.4. Reads
// GET /conversations/oversight: every conversation currently handed off to a
// person, with who holds it and how long it has waited, so the manager keeps
// visibility of the leads on the floor.

import { apiClient } from '../../api/client.js'

export interface OversightItem {
  id: string
  contact: { name: string; phone: string; initials: string }
  state: 'waiting_handoff' | 'active_handoff'
  stateLabel: string
  /** Holder's name, or null when still in the pool. */
  holder: string | null
  inPool: boolean
  /** Minutes in the current handoff state. */
  waitingMinutes: number
  lastActivityMinutes: number | null
  /** The customer sent the last message — we owe a reply. */
  waitingOnUs: boolean
  /** Per-stage SLA breach flags (M4-L3). */
  sla: { firstResponse: boolean; idle: boolean; resolution: boolean; any: boolean }
  stateEnteredAt: string
  assignedAt: string | null
  lastMessageAt: string | null
}

/**
 * GET /conversations/oversight — every conversation currently handed off, with
 * holder, wait time and per-stage SLA breach flags for the Supervisión view.
 */
export async function getHandoffOversight(): Promise<OversightItem[]> {
  const res = await apiClient.get<{ data: OversightItem[] }>('/conversations/oversight')
  return res.data.data
}

/** Manager action: pull a held conversation back into the unassigned queue (the
 *  Asesor is out / sitting on it), so another Asesor can claim it. */
export async function returnToPool(conversationId: string): Promise<void> {
  await apiClient.post(`/conversations/${encodeURIComponent(conversationId)}/return-to-pool`)
}

export interface AssignableMember {
  id: string
  name: string
  role: string
}

/** The team members a conversation can be assigned to — active members who can
 *  handle conversations (owners/admins/Asesores, not read-only viewers).
 *
 * GET /team/assignable, not GET /settings: the settings payload carries the
 * full team roster with emails, ~40 audit-log rows and the tenant's fiscal
 * identity, which is manage-only (owner/admin). Every non-viewer member needs
 * this list to reassign a conversation, so it has its own lean endpoint that
 * returns only id/name/role. */
export async function getAssignableMembers(): Promise<AssignableMember[]> {
  const res = await apiClient.get<{ data: AssignableMember[] }>('/team/assignable')
  return res.data.data
}

/** Manager action: assign a handed-off conversation to a specific Asesor. */
export async function assignConversation(conversationId: string, agentId: string): Promise<void> {
  await apiClient.post(`/conversations/${encodeURIComponent(conversationId)}/assign`, { agentId })
}

/** Humanize a minute count for the manager view: "5 min", "1 h 20 min",
 *  "2 días". null → "—". */
export function durationLabel(minutes: number | null): string {
  if (minutes === null) return '—'
  if (minutes < 1) return 'ahora'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    const rem = minutes % 60
    return rem === 0 ? `${hours} h` : `${hours} h ${rem} min`
  }
  const days = Math.floor(hours / 24)
  return days === 1 ? '1 día' : `${days} días`
}
